import * as HoverCard from "@radix-ui/react-hover-card";
import { Skeleton, Tooltip } from "antd";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { MeasurementTypes } from "../../../../../../@types/Measurements";
import { ArtiQAccepted } from "../../../../../../assets/icons/ArtiQAccepted";
import { ArtiQRejected } from "../../../../../../assets/icons/ArtiQRejected";
import { ArtiQUsableAccepted } from "../../../../../../assets/icons/ArtiQUsableAccepted";
import { Delete } from "../../../../../../assets/icons/Delete";
import { Undo } from "../../../../../../assets/icons/Undo";
import { CustomIconButton } from "../../../../../../components/CustomIconButton";
import { tooltipTitles } from "../../../../../../constants/measurements";
import { artiQAcceptabilityStatus } from "../../../../../../constants/spirometryConstants";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import {
  getLongFormattedDate,
  LONG_DATE_TIME_FORMAT,
} from "../../../../../../utils/dateFormatter";
import { roundToX } from "../../../../../../utils/unitOperations";
import { useArchiveMeasurement } from "../../hooks/useArchiveMeasurement";
import { ArchiveSpirometryConfirmationModal } from "../ArchiveSpirometryModal";
import {
  archiveButtonCss,
  BlowColorDot,
  ListBlowHoverCardContent,
  ListBlowItem,
  skeletonCss,
  SpirometryListTitle,
} from "./BlowAcceptabilitySection.styles";

export type BlowAcceptabilityProps = {
  fvcAcceptabilityReason?: string[];
  fev1Acceptability?: string;
  fvcAcceptability?: string;
  checked?: boolean;
};

export interface IBlowAcceptability {
  blows?: BlowAcceptabilityProps[];
  spirometryData?: any;
  hoveredBlowIndex?: number;
  selectedBlowIndex?: number;
  onRowHover?: (index?: number) => void;
  archivedSpirometryTestsIds: number[];
  measurementType: MeasurementTypes;
  unit?: string;
  patientId?: string;
  refetch?: () => void;
}

const criteriasConstants = {
  EOFE_FALSE: "Failure of EOFE criteria",
  BEV_FALSE_1: "Excessive hesitation causing failure of BEV criteria",
  BEV_FALSE_2: "Artefact (P=",
};

export function BlowAcceptabilitySection({
  blows,
  spirometryData,
  hoveredBlowIndex,
  selectedBlowIndex,
  archivedSpirometryTestsIds,
  unit,
  measurementType,
  patientId,
  onRowHover,
  refetch,
}: IBlowAcceptability) {
  const { t } = useTranslation();

  const [spirometryBlowToArchive, setSpirometryBlowToArchive] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isArchiveButtonHovered, setIsArchivedButtonHovered] =
    useState<boolean>(false);

  const { archiveMeasurement, isRefetching } = useArchiveMeasurement();

  const { isMobile } = useIsMobile();

  const acceptabilityCriteria = (fvcAcceptabilityReason: any) => {
    const criteria = {
      EOFE: t("patientPage_measurementModal_blow_true"),
      BEV: t("patientPage_measurementModal_blow_true"),
    };

    const acceptabilityReason = fvcAcceptabilityReason;

    if (!acceptabilityReason) {
      criteria.BEV = "-";
      criteria.EOFE = "-";

      return criteria;
    }

    if (
      acceptabilityReason.some((reason: string) =>
        reason.includes(criteriasConstants.EOFE_FALSE)
      )
    ) {
      criteria.EOFE = t("patientPage_measurementModal_blow_false");
    }

    if (
      acceptabilityReason.some((reason: string) =>
        reason.includes(criteriasConstants.BEV_FALSE_1)
      ) ||
      acceptabilityReason.some((reason: string) =>
        reason.includes(criteriasConstants.BEV_FALSE_2)
      )
    ) {
      criteria.BEV = t("patientPage_measurementModal_blow_false");
    }

    return criteria;
  };

  const blowStatus = (fev1Acceptability: any, fvcAcceptability: any) => {
    if (!fev1Acceptability || !fvcAcceptability)
      return { icon: null, status: "-" };

    if (
      fev1Acceptability === artiQAcceptabilityStatus.ACCEPTABLE &&
      fvcAcceptability === artiQAcceptabilityStatus.ACCEPTABLE
    ) {
      return {
        icon: <ArtiQAccepted />,
        status: t("patientPage_measurementModal_blow_accepted"),
      };
    }

    if (
      (fev1Acceptability === artiQAcceptabilityStatus.REJECTED &&
        fvcAcceptability === artiQAcceptabilityStatus.ACCEPTABLE) ||
      (fev1Acceptability === artiQAcceptabilityStatus.REJECTED &&
        fvcAcceptability === artiQAcceptabilityStatus.USABLE) ||
      (fev1Acceptability === artiQAcceptabilityStatus.REJECTED &&
        fvcAcceptability === artiQAcceptabilityStatus.REJECTED)
    ) {
      return {
        icon: <ArtiQRejected />,
        status: t("patientPage_measurementModal_blow_rejected"),
      };
    }

    return {
      icon: <ArtiQUsableAccepted />,
      status: t("patientPage_measurementModal_blow_usable"),
    };
  };

  const lineColors = [
    "#8974D4",
    "#69B6E1",
    "#DAA676",
    "#89D7AB",
    "#CE75C5",
    "#F5B0C0",
    "#FDDF94",
    "#939393",
  ];

  const handleSpirometryData = () => {
    const dataToReturn: ReactElement[] = [];

    if (hoveredBlowIndex !== undefined) {
      const data = spirometryData[hoveredBlowIndex];

      if (data.fvc !== undefined) {
        dataToReturn.push(<p>FVC (L): {data.fvc}</p>);

        if (data.calculated_predicted_fvc !== undefined) {
          dataToReturn.push(
            <p>
              FVC% pred:{" "}
              {roundToX((data.fvc / data.calculated_predicted_fvc) * 100, 2)}
            </p>
          );
        }
      }

      if (data.fev1 !== undefined) {
        dataToReturn.push(<p>FEV1 (L): {data.fev1}</p>);

        if (data.calculated_predicted_fev1 !== undefined) {
          dataToReturn.push(
            <p>
              FEV1% pred:{" "}
              {roundToX((data.fev1 / data.calculated_predicted_fev1) * 100, 2)}
            </p>
          );
        }
      }

      if (data.pef !== undefined) {
        dataToReturn.push(<p>PEF (L/sec): {data.pef}</p>);
      }

      if (data.fef25_75 !== undefined) {
        dataToReturn.push(<p>FEF 25/75: {data.fef25_75}</p>);
      }

      dataToReturn.push(<p>{getLongFormattedDate(data.start_time)}</p>);
    }

    return dataToReturn;
  };

  const checkIfBlowIsArchivedByIndex = (index: number) => {
    return archivedSpirometryTestsIds.includes(
      spirometryData[index].spirometry_test_id
    );
  };

  const handleArchiveBlow = async (confirm: boolean) => {
    if (patientId === undefined) {
      setSpirometryBlowToArchive(undefined);
      return;
    }

    if (confirm && spirometryBlowToArchive) {
      setIsLoading(true);
      await archiveMeasurement(
        spirometryBlowToArchive.patient_measurement_id,
        true,
        measurementType,
        patientId,
        tooltipTitles.get(measurementType)
      );
      if (refetch) {
        refetch();
      }
      setIsLoading(false);
    }

    setSpirometryBlowToArchive(undefined);
  };

  const handleUnarchiveBlow = async (spirometryId: number) => {
    if (patientId === undefined) return;

    setIsLoading(true);
    await archiveMeasurement(
      spirometryId,
      false,
      measurementType,
      patientId,
      tooltipTitles.get(measurementType)
    );
    if (refetch) {
      refetch();
    }
    setIsLoading(false);
  };

  const archiveBlowButton = (index: number) => {
    const isArchived = checkIfBlowIsArchivedByIndex(index);

    const icon = isArchived ? <Undo /> : <Delete />;

    const isHovered = index === hoveredBlowIndex;

    return (
      <Tooltip
        title={t(
          isArchived
            ? "patientPage_measurementModal_blows_restore"
            : "patientPage_measurementModal_blows_archive"
        )}
        zIndex={3002}
        color="#4B4B4B"
        arrow={false}
        styles={{
          body: {
            minHeight: "38px",
            minWidth: "157px",
            borderRadius: "4px !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          },
        }}
      >
        <CustomIconButton
          className={archiveButtonCss()}
          onClick={() => {
            if (isArchived) {
              handleUnarchiveBlow(spirometryData[index].patient_measurement_id);
            } else {
              setSpirometryBlowToArchive(spirometryData[index]);
            }
          }}
          onMouseEnter={() => {
            setIsArchivedButtonHovered(true);
          }}
          onMouseLeave={() => {
            setIsArchivedButtonHovered(false);
          }}
        >
          {isHovered && icon}
        </CustomIconButton>
      </Tooltip>
    );
  };

  return isRefetching ? (
    <Skeleton
      style={{ marginTop: "84px" }}
      title={false}
      paragraph={{ rows: 5, width: "85%", style: { height: "20px" } }}
      className={skeletonCss()}
    />
  ) : (
    <div style={{ position: "relative", columnGap: "1px", marginTop: "60px" }}>
      <SpirometryListTitle>
        {isMobile ? (
          <div
            style={{
              width: "calc(50% - 25px)",
            }}
          />
        ) : null}
        <p>{t("patientPage_measurementModal_blow_accept")}</p>
      </SpirometryListTitle>
      {blows !== undefined
        ? blows.map((blow, index) => (
            <HoverCard.Root key={index} open>
              <HoverCard.Trigger>
                <ListBlowItem
                  isHovered={index === hoveredBlowIndex}
                  isOpaque={
                    hoveredBlowIndex === undefined
                      ? index !== selectedBlowIndex
                      : index !== hoveredBlowIndex
                  }
                  onMouseEnter={() => {
                    if (onRowHover) {
                      onRowHover(index);
                    }
                  }}
                  onMouseLeave={() => {
                    if (onRowHover) {
                      onRowHover(undefined);
                    }
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "10px",
                      width: "50%",
                      position: "relative",

                      opacity:
                        checkIfBlowIsArchivedByIndex(index) &&
                        index === hoveredBlowIndex &&
                        index !== selectedBlowIndex
                          ? 0.5
                          : 1,

                      ...(checkIfBlowIsArchivedByIndex(index) && {
                        backgroundImage:
                          "linear-gradient(var(--colors-gray-80), var(--colors-gray-80))",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "100% 1px",
                        backgroundPosition: "left center",
                      }),
                    }}
                  >
                    <p>
                      {dayjs(spirometryData[index].start_time).format(
                        "HH:mm:ss"
                      )}
                    </p>
                    <BlowColorDot
                      style={{ backgroundColor: lineColors[index] }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "20%",
                    }}
                  >
                    {
                      blowStatus(blow.fev1Acceptability, blow.fvcAcceptability)
                        .icon
                    }
                  </div>
                  <div
                    style={{
                      width: "30px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    {archiveBlowButton(index)}
                  </div>
                </ListBlowItem>
              </HoverCard.Trigger>
              {index === hoveredBlowIndex && !isArchiveButtonHovered ? (
                <ListBlowHoverCardContent>
                  <p>
                    {t("patientPage_measurementModal_blow_status", {
                      status: blowStatus(
                        blow.fev1Acceptability,
                        blow.fvcAcceptability
                      ).status,
                    })}
                  </p>
                  <p>
                    {t("patientPage_measurementModal_blow_bevCriteria", {
                      value: acceptabilityCriteria(blow.fvcAcceptabilityReason)
                        .BEV,
                    })}
                  </p>
                  <p>
                    {t("patientPage_measurementModal_blow_eofeCriteria", {
                      value: acceptabilityCriteria(blow.fvcAcceptabilityReason)
                        .EOFE,
                    })}
                  </p>
                  {handleSpirometryData()}
                </ListBlowHoverCardContent>
              ) : null}
            </HoverCard.Root>
          ))
        : null}
      {spirometryBlowToArchive && (
        <ArchiveSpirometryConfirmationModal
          archiveMeasurementValue={spirometryBlowToArchive[measurementType]}
          isLoading={isLoading}
          unit={unit}
          time={dayjs(spirometryBlowToArchive.start_time).format(
            LONG_DATE_TIME_FORMAT
          )}
          handleConfirmModalClose={handleArchiveBlow}
        />
      )}
    </div>
  );
}
